import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Section } from "@/components/Section";
import { supabase } from "@/integrations/supabase/client";
import { getPostForEdit, savePost, isAdmin, searchPexels, uploadPostImage } from "@/lib/admin.functions";
import { listTeam } from "@/lib/content.functions";

export const Route = createFileRoute("/admin/post/$id")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Editar post | SVG" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PostEditor,
});

type FormState = {
  slug: string;
  title: string;
  description: string;
  excerpt: string;
  category: string;
  cover: string;
  body: string;
  author: string;
  reading_time: string;
  published: boolean;
  cover_image: string;
  youtube_urls: string;
  keywords: string;
};

const empty: FormState = {
  slug: "",
  title: "",
  description: "",
  excerpt: "",
  category: "SEO",
  cover: "linear-gradient(135deg,#005BFF,#0a1a3a)",
  body: "",
  author: "Equipe SVG Multimídia",
  reading_time: "5 min",
  published: false,
  cover_image: "",
  youtube_urls: "",
  keywords: "",
};

function PostEditor() {
  const { id } = useParams({ from: "/admin/post/$id" });
  const navigate = useNavigate();
  const isNew = id === "new";
  const [form, setForm] = useState<FormState>(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user) {
        navigate({ to: "/auth", replace: true });
        return;
      }
      try {
        const a = await isAdmin();
        if (!a.admin) {
          setAllowed(false);
          setLoading(false);
          return;
        }
        setAllowed(true);
        if (!isNew) {
          const post = await getPostForEdit({ data: { id } });
          if (post) {
            setForm({
              slug: post.slug, title: post.title, description: post.description,
              excerpt: post.excerpt ?? "", category: post.category,
              cover: post.cover ?? empty.cover, body: post.body ?? "",
              author: post.author ?? empty.author, reading_time: post.reading_time ?? "5 min",
              published: Boolean(post.published),
              cover_image: (post as any).cover_image ?? "",
              youtube_urls: ((post as any).youtube_urls ?? []).join("\n"),
              keywords: ((post as any).keywords ?? []).join(", "),
            });
          }
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erro");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew, navigate]);

  function update<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function slugify(value: string) {
    return value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .slice(0, 120);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const payload = {
        ...form,
        cover_image: form.cover_image.trim() || null,
        youtube_urls: form.youtube_urls.split(/\s+/).map((s) => s.trim()).filter(Boolean),
        keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
        ...(isNew ? {} : { id }),
      };
      await savePost({ data: payload });
      navigate({ to: "/admin" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  }

  // Pexels search
  const [pexelsQuery, setPexelsQuery] = useState("");
  const [pexelsResults, setPexelsResults] = useState<Array<{ url: string; photographer: string }>>([]);
  const [pexelsLoading, setPexelsLoading] = useState(false);
  const [team, setTeam] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    listTeam().then((rows) => setTeam(rows.map((r) => ({ id: r.id, name: r.name })))).catch(() => {});
  }, []);
  async function runPexelsSearch() {
    if (!pexelsQuery.trim()) return;
    setPexelsLoading(true);
    try {
      const r = await searchPexels({ data: { query: pexelsQuery.trim() } });
      setPexelsResults(r as any);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Pexels falhou");
    } finally {
      setPexelsLoading(false);
    }
  }

  // ───── Body editing helpers: nofollow links, Google Maps embed, local image upload ─────
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  function insertAtCursor(text: string) {
    const el = bodyRef.current;
    if (!el) {
      update("body", form.body + text);
      return;
    }
    const start = el.selectionStart ?? form.body.length;
    const end = el.selectionEnd ?? form.body.length;
    const next = form.body.slice(0, start) + text + form.body.slice(end);
    update("body", next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + text.length;
      el.setSelectionRange(pos, pos);
    });
  }

  function insertNofollowLink() {
    const el = bodyRef.current;
    const selected = el ? form.body.slice(el.selectionStart ?? 0, el.selectionEnd ?? 0) : "";
    const anchor = (window.prompt("Texto do link:", selected) || "").trim();
    if (!anchor) return;
    const url = (window.prompt("URL (https://...)") || "").trim();
    if (!url) return;
    const html = `<a href="${url}" rel="nofollow noopener" target="_blank">${anchor}</a>`;
    if (el && (el.selectionEnd ?? 0) > (el.selectionStart ?? 0)) {
      const start = el.selectionStart!;
      const end = el.selectionEnd!;
      update("body", form.body.slice(0, start) + html + form.body.slice(end));
    } else {
      insertAtCursor(html);
    }
  }

  function insertGoogleMap() {
    const raw = (window.prompt("Cole o iframe ou a URL de incorporação do Google Maps:") || "").trim();
    if (!raw) return;
    let iframe = raw;
    // Allow a bare URL — wrap it.
    if (!/^<iframe/i.test(raw)) {
      iframe = `<iframe src="${raw}" width="100%" height="420" style="border:0" loading="lazy" allowfullscreen referrerpolicy="no-referrer-when-downgrade"></iframe>`;
    }
    const block = `\n\n<div class="map-embed rounded-2xl overflow-hidden border border-border my-4">${iframe}</div>\n\n`;
    insertAtCursor(block);
  }

  const [uploadingImg, setUploadingImg] = useState<"body" | "cover" | null>(null);
  async function handleLocalImageUpload(file: File, target: "body" | "cover") {
    if (!file) return;
    setUploadingImg(target);
    try {
      const buf = await file.arrayBuffer();
      let bin = "";
      const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      const base64 = btoa(bin);
      const r = await uploadPostImage({ data: {
        fileName: file.name, contentType: file.type || "image/jpeg", base64,
      } });
      if (target === "cover") {
        update("cover_image", r.url);
      } else {
        insertAtCursor(`\n\n![${file.name}](${r.url})\n\n`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha no upload");
    } finally {
      setUploadingImg(null);
    }
  }

  if (loading) return <Section><p className="text-sm text-muted-foreground">Carregando...</p></Section>;
  if (!allowed) return <Section><p className="text-sm text-destructive">Sem permissão.</p></Section>;

  return (
    <Section>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">{isNew ? "Novo post" : "Editar post"}</h1>
        <button type="button" onClick={() => navigate({ to: "/admin" })} className="text-sm text-muted-foreground hover:text-foreground">← Voltar</button>
      </div>

      <form onSubmit={onSubmit} className="card-glass space-y-4">
        <div>
          <label className="text-sm font-medium">Título</label>
          <input
            required value={form.title}
            onChange={(e) => {
              update("title", e.target.value);
              if (isNew && !form.slug) update("slug", slugify(e.target.value));
            }}
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm"
          />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-medium">Slug</label>
            <input required value={form.slug} onChange={(e) => update("slug", slugify(e.target.value))}
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
          </div>
          <div>
            <label className="text-sm font-medium">Categoria</label>
            <input required value={form.category} onChange={(e) => update("category", e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">Meta description (SEO)</label>
          <textarea required maxLength={300} rows={2} value={form.description}
            onChange={(e) => update("description", e.target.value)}
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium">Resumo (excerpt)</label>
          <textarea rows={2} value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)}
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="text-sm font-medium">Autor</label>
            <select value={form.author} onChange={(e) => update("author", e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm">
              <option value="Equipe SVG Multimídia">Equipe SVG Multimídia</option>
              {team.map((m) => (
                <option key={m.id} value={m.name}>{m.name}</option>
              ))}
              {form.author &&
                form.author !== "Equipe SVG Multimídia" &&
                !team.some((m) => m.name === form.author) && (
                  <option value={form.author}>{form.author}</option>
                )}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Tempo de leitura</label>
            <input value={form.reading_time} onChange={(e) => update("reading_time", e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium">Capa (URL ou CSS gradient)</label>
            <input value={form.cover} onChange={(e) => update("cover", e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">Conteúdo (parágrafos separados por linha em branco)</label>
          <div className="mt-1 mb-2 flex flex-wrap gap-2">
            <button type="button" onClick={insertNofollowLink}
              className="rounded-lg border border-border bg-background/60 px-3 py-1.5 text-xs hover:border-primary">
              🔗 Inserir link (nofollow)
            </button>
            <button type="button" onClick={insertGoogleMap}
              className="rounded-lg border border-border bg-background/60 px-3 py-1.5 text-xs hover:border-primary">
              🗺️ Inserir Google Maps
            </button>
            <label className="cursor-pointer rounded-lg border border-border bg-background/60 px-3 py-1.5 text-xs hover:border-primary">
              {uploadingImg === "body" ? "Enviando..." : "🖼️ Upload de imagem no corpo"}
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleLocalImageUpload(f, "body");
                  e.currentTarget.value = "";
                }} />
            </label>
          </div>
          <textarea ref={bodyRef} rows={14} value={form.body} onChange={(e) => update("body", e.target.value)}
            className="w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
          <p className="mt-1 text-xs text-muted-foreground">Suporta Markdown e HTML. Links inseridos pelo botão são automaticamente <code>rel="nofollow noopener"</code> e abrem em nova aba.</p>
        </div>

        <div className="space-y-2 rounded-xl border border-border shadow-[var(--shadow-soft)] p-4">
          <label className="text-sm font-medium">Imagem de capa (URL)</label>
          <input value={form.cover_image} onChange={(e) => update("cover_image", e.target.value)}
            placeholder="https://..."
            className="w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
          <div className="flex items-center gap-2">
            <label className="cursor-pointer rounded-lg border border-border bg-background/60 px-3 py-1.5 text-xs hover:border-primary">
              {uploadingImg === "cover" ? "Enviando..." : "📤 Upload local de capa"}
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleLocalImageUpload(f, "cover");
                  e.currentTarget.value = "";
                }} />
            </label>
            <span className="text-[11px] text-muted-foreground">PNG/JPG/WebP até ~10 MB.</span>
          </div>
          {form.cover_image && (
            <img src={form.cover_image} alt="Capa" className="mt-2 h-32 w-full rounded-lg object-cover" loading="lazy" />
          )}
          <div className="mt-3 flex gap-2">
            <input value={pexelsQuery} onChange={(e) => setPexelsQuery(e.target.value)}
              placeholder="Buscar imagem no Pexels..."
              className="flex-1 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            <button type="button" onClick={runPexelsSearch} disabled={pexelsLoading}
              className="rounded-lg border border-border px-4 py-2 text-sm">
              {pexelsLoading ? "Buscando..." : "Buscar"}
            </button>
          </div>
          {pexelsResults.length > 0 && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {pexelsResults.map((p, i) => (
                <button key={i} type="button" onClick={() => update("cover_image", p.url)}
                  className={`overflow-hidden rounded-lg border-2 transition ${form.cover_image === p.url ? "border-primary" : "border-transparent hover:border-border"}`}>
                  <img src={p.url} alt={p.photographer} className="h-24 w-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-medium">URLs do YouTube (uma por linha)</label>
            <textarea rows={3} value={form.youtube_urls} onChange={(e) => update("youtube_urls", e.target.value)}
              placeholder={"https://youtu.be/...\nhttps://www.youtube.com/watch?v=..."}
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
            <p className="mt-1 text-xs text-muted-foreground">Serão embutidos como vídeos ao final do artigo.</p>
          </div>
          <div>
            <label className="text-sm font-medium">Palavras-chave (separadas por vírgula)</label>
            <textarea rows={3} value={form.keywords} onChange={(e) => update("keywords", e.target.value)}
              placeholder="seo local, ficha do google, marketing digital"
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.published} onChange={(e) => update("published", e.target.checked)} />
          Publicar agora
        </label>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => navigate({ to: "/admin" })} className="rounded-lg border border-border px-4 py-2 text-sm">Cancelar</button>
          <button type="submit" disabled={saving} className="btn-primary">{saving ? "Salvando..." : "Salvar"}</button>
        </div>
      </form>
    </Section>
  );
}